import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import type { StandardCart } from '@/types/ShopFront/CheckoutStandards'
import { error } from '../Log'

type CartOperatorResponse = Promise<{ success: boolean, newCart: StandardCart | null }>
const withExtendWarrantyNormalization = <T>(
  operator: (...args: T[]) => CartOperatorResponse,
) => async (...args: T[]) => {
    const [
      { getCart },
      { checkIsExtendItem },
      { EXTEND_SKU },
      { rawCartDeleteItem },
      { rawCartUpdateQuantity },
    ] = await allPromisesWithRetries(() => [
      import('@/services/Cart/getCart'),
      import('@/helpers/checkIsExtendItem'),
      import('@/data/constants'),
      import('@/services/Cart/rawCartDeleteItem'),
      import('@/services/Cart/rawCartUpdateQuantity'),
    ])
    try {
      const cartOperationResult = await operator(...args)
      if (!cartOperationResult.success || !cartOperationResult.newCart) {
        return cartOperationResult
      }
      const getExtendingSku = (sku: string) => (
        checkIsExtendItem({ sku })
          ? sku.split(EXTEND_SKU)[1]
          : ''
      )
      const extendWarrantyItems = cartOperationResult.newCart.lineItems.filter(checkIsExtendItem)
      const sumQuantitiesWithSku = (target: string) => (
        cartOperationResult.newCart?.lineItems.reduce((acc, { sku, quantity }) => (
          sku === target
            ? acc + quantity
            : acc
        ), 0) || 0
      )
      type IDAndQuantitTuple = [string, number]
      const shouldBeUpdated = (x: IDAndQuantitTuple | null): x is IDAndQuantitTuple => !!x
      const idsToAlsoApllyOperator: IDAndQuantitTuple[] = (
        extendWarrantyItems.map((item) => {
          const expectedquantity = sumQuantitiesWithSku(getExtendingSku(item.sku))
          if (expectedquantity !== item.quantity) {
            return [item.id, expectedquantity]
          }
          return null
        }).filter(shouldBeUpdated)
      )
      if (!idsToAlsoApllyOperator.length) {
        return cartOperationResult
      }
      const cartId = cartOperationResult.newCart.id
      const promises = await Promise.all(idsToAlsoApllyOperator.map(([itemId, quantity]) => (
        quantity > 0
          ? rawCartUpdateQuantity({ cartId, itemId, quantity })
          : rawCartDeleteItem({ cartId, itemId })
      )))
      const lastCart = promises.sort(({ newCart: cartA }, { newCart: cartB }) => {
        if (cartA && !cartB) {
          return 1
        }
        if (!cartA && cartB) {
          return -1
        }
        if (cartA && cartB) {
          return (
            new Date(cartA.updatedTime) > new Date(cartB.updatedTime)
              ? 1
              : -1
          )
        }
        return 0
      })[0]
      return { success: true, newCart: lastCart.newCart }
    } catch (err) {
      error('Failed to Normalize cart with Extend')
    }
    return { success: false, newCart: await getCart() }
  }

export default withExtendWarrantyNormalization
